import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Globe,
  Plus,
  Play,
  Square,
  Pencil,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Radio,
  Search,
  X,
  FileText,
  Database,
  HardDrive,
  Hash,
  ExternalLink,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
} from 'lucide-react';
import {
  useSources,
  useScrapeJobs,
  useJobPoller,
  type Source,
  type ScrapeJob,
} from '@/hooks/useSources';
import { useUser } from '@/hooks/useUser';
import { SourceHealthModal } from './_components/SourceHealthModal';
import { useSourceStaleness } from '@/hooks/useSourceStaleness';
import { apiFetch } from '@/lib/apiFetch';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  SourceDialog,
  buildCrawlConfig,
  SCHEDULES,
} from '@/components/sources/CreateSourceDialog';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toScheduleCron(v: string): string | null {
  return v === 'none' || v === '' ? null : v;
}

/** Convert a DB cron string (or null) to the form select value */

function statusBadge(status: string) {
  const map: Record<
    string,
    {
      label: string;
      variant: 'default' | 'secondary' | 'destructive' | 'outline';
      className?: string;
    }
  > = {
    queued: { label: 'Queued', variant: 'secondary' },
    running: {
      label: 'Running',
      variant: 'default',
      className: 'bg-blue-600 text-white animate-pulse',
    },
    succeeded: {
      label: 'Succeeded',
      variant: 'outline',
      className: 'border-green-300 text-green-700 bg-green-50',
    },
    done: {
      label: 'Succeeded',
      variant: 'outline',
      className: 'border-green-300 text-green-700 bg-green-50',
    },
    failed: { label: 'Failed', variant: 'destructive' },
    partial: {
      label: 'Partial',
      variant: 'outline',
      className: 'border-amber-300 text-amber-700 bg-amber-50',
    },
  };
  const cfg = map[status] ?? { label: status, variant: 'secondary' as const };
  return (
    <Badge variant={cfg.variant} className={cfg.className}>
      {cfg.label}
    </Badge>
  );
}

function needsReview(job: ScrapeJob): boolean {
  const terminated = job.status !== 'queued' && job.status !== 'running';
  if (!terminated) return false;
  const pages = job.stats?.pages_crawled ?? job.stats?.pagesCrawled ?? 0;
  if (pages === 0) return true;
  if (job.started_at && job.finished_at) {
    const ms =
      new Date(job.finished_at).getTime() - new Date(job.started_at).getTime();
    if (ms < 60_000) return true;
  }
  return false;
}

function fmtDate(s: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

// ── Scrape job detail drawer ──────────────────────────────────────────────────

function fmtBytes(n: number): string {
  if (n === 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function duration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

interface JobDrawerProps {
  job: ScrapeJob;
  hasPrev?: boolean;
  hasNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onClose: () => void;
}

function JobDrawer({
  job,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: JobDrawerProps) {
  const [copied, setCopied] = useState(false);

  // Escape closes; Left/Right arrows navigate records
  React.useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) onPrev();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, hasPrev, hasNext, onPrev, onNext]);

  function copyId() {
    void navigator.clipboard.writeText(job.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  // Support both snake_case (current) and camelCase (legacy jobs before the fix)
  const stats = {
    pages_crawled: job.stats?.pages_crawled ?? job.stats?.pagesCrawled ?? 0,
    artifacts_found:
      job.stats?.artifacts_found ?? job.stats?.artifactsFound ?? 0,
    new_artifacts: job.stats?.new_artifacts ?? job.stats?.newArtifacts ?? 0,
    bytes_stored: job.stats?.bytes_stored ?? job.stats?.bytesStored ?? 0,
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 min-w-0">
            {statusBadge(job.status)}
            <span className="text-sm font-medium text-gray-700 truncate">
              {job.sources?.name ?? 'Unknown source'}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onPrev}
              disabled={!hasPrev}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:pointer-events-none"
              aria-label="Previous record"
              title="Previous record (←)"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:pointer-events-none"
              aria-label="Next record"
              title="Next record (→)"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4 space-y-6 text-sm">
          {/* Source & job ID */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Job
            </h3>
            <div className="space-y-1.5">
              {job.sources?.base_url && (
                <div className="flex items-center gap-2 text-gray-700">
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                  <a
                    href={job.sources.base_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                  >
                    {job.sources.base_url}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-500">
                <Hash className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="font-mono text-xs break-all">{job.id}</span>
                <button
                  onClick={copyId}
                  className="ml-auto p-1 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
                  title="Copy job ID"
                >
                  {copied ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </section>

          {/* Timing */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Timing
            </h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { label: 'Created', value: fmtDate(job.created_at) },
                {
                  label: 'Duration',
                  value: duration(job.started_at, job.finished_at),
                },
                { label: 'Started', value: fmtDate(job.started_at) },
                { label: 'Finished', value: fmtDate(job.finished_at) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-xs text-gray-400">{label}</dt>
                  <dd className="text-gray-800 font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Stats */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Processing stats
              </h3>
              {job.source_id && (
                <Link
                  to={`/artifacts?source=${job.source_id}`}
                  onClick={onClose}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <ExternalLink className="w-3 h-3" />
                  View in Artifacts
                </Link>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  icon: <FileText className="w-4 h-4 text-blue-500" />,
                  label: 'Pages crawled',
                  value: stats.pages_crawled.toLocaleString(),
                },
                {
                  icon: <Database className="w-4 h-4 text-purple-500" />,
                  label: 'Artifacts found',
                  value: stats.artifacts_found.toLocaleString(),
                },
                {
                  icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
                  label: 'New artifacts',
                  value: stats.new_artifacts.toLocaleString(),
                },
                {
                  icon: <HardDrive className="w-4 h-4 text-orange-500" />,
                  label: 'Stored',
                  value: fmtBytes(stats.bytes_stored),
                },
              ].map(({ icon, label, value }) => (
                <div
                  key={label}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2"
                >
                  {icon}
                  <div>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-base font-semibold text-gray-900">
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Error */}
          {job.error && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wide flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Error
              </h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <pre className="text-xs text-red-700 whitespace-pre-wrap font-mono break-all">
                  {job.error}
                </pre>
              </div>
            </section>
          )}

          {/* Raw stats JSON (debug) */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Raw stats
            </h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap">
                {JSON.stringify(job.stats, null, 2)}
              </pre>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// ── Jobs panel ────────────────────────────────────────────────────────────────

export function JobsPanel({
  sourceId,
  onRefetchReady,
}: {
  sourceId?: string;
  onRefetchReady?: (fn: () => void) => void;
}) {
  const { jobs, loading, error, refetch } = useScrapeJobs(sourceId);
  const [searchParams, setSearchParams] = useSearchParams();

  // Expose refetch to the parent so the Refresh button can trigger it
  useEffect(() => {
    onRefetchReady?.(refetch);
  }, [refetch, onRefetchReady]);

  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  // Derive selected job from index (URL ?job= kept for deep-linking on open)
  const selectedJobId = searchParams.get('job');
  // Prefer URL param on first load, then follow selectedIdx
  const selected =
    selectedIdx !== null
      ? (jobs[selectedIdx] ?? null)
      : (jobs.find((j) => j.id === selectedJobId) ?? null);

  // Sync selectedIdx from URL param on initial load
  React.useEffect(() => {
    if (selectedJobId && selectedIdx === null) {
      const idx = jobs.findIndex((j) => j.id === selectedJobId);
      if (idx !== -1) setSelectedIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  function openJob(j: ScrapeJob, idx: number) {
    setSelectedIdx(idx);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'jobs');
      next.set('job', j.id);
      return next;
    });
  }

  function closeJob() {
    setSelectedIdx(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('job');
      return next;
    });
  }

  function navigateJob(idx: number) {
    if (idx < 0 || idx >= jobs.length) return;
    openJob(jobs[idx], idx);
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-red-200 rounded-lg bg-red-50">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
        <p className="text-red-900 font-medium mb-2">Failed to Load Jobs</p>
        <p className="text-sm text-red-700 mb-4">
          {error.includes('429') || error.includes('Too many requests')
            ? 'Too many requests. Please wait and try again.'
            : error.includes('Failed to fetch')
              ? 'Unable to connect to server.'
              : error}
        </p>
        <Button onClick={() => refetch()} size="sm" variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p>No scrape jobs yet.</p>
      </div>
    );
  }

  return (
    <>
      {selected && (
        <JobDrawer
          job={selected}
          hasPrev={selectedIdx !== null && selectedIdx > 0}
          hasNext={selectedIdx !== null && selectedIdx < jobs.length - 1}
          onPrev={() => selectedIdx !== null && navigateJob(selectedIdx - 1)}
          onNext={() => selectedIdx !== null && navigateJob(selectedIdx + 1)}
          onClose={closeJob}
        />
      )}
      <p className="text-xs text-gray-500 mb-3">
        Click any row to see full details, stats, and error logs.
      </p>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden text-gray-900">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-100 border-b-2 border-gray-300 hover:bg-gray-100 pointer-events-none">
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Job ID
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Source
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Status
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                New artifacts
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Pages
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Duration
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Started
              </TableHead>
              <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                Error
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((j, idx) => (
              <TableRow
                key={j.id}
                className={`cursor-pointer border-b border-gray-100 transition-colors ${
                  selectedIdx === idx
                    ? 'bg-blue-100 ring-1 ring-inset ring-blue-400'
                    : 'bg-white hover:bg-blue-50'
                }`}
                onClick={() => openJob(j, idx)}
              >
                <TableCell>
                  <span className="font-mono text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded select-all">
                    {j.id.slice(0, 8)}
                  </span>
                </TableCell>
                <TableCell className="font-medium text-gray-900">
                  {j.sources?.name ?? j.source_id.slice(0, 8)}
                </TableCell>
                <TableCell>
                  {needsReview(j) ? (
                    <Badge
                      variant="outline"
                      className="border-orange-300 bg-orange-50 text-orange-700 shrink-0 whitespace-nowrap"
                    >
                      Review Req
                    </Badge>
                  ) : (
                    statusBadge(j.status)
                  )}
                </TableCell>
                <TableCell className="text-gray-800">
                  {j.stats?.new_artifacts ?? j.stats?.newArtifacts ?? 0}
                </TableCell>
                <TableCell className="text-gray-600">
                  {j.stats?.pages_crawled ?? j.stats?.pagesCrawled ?? 0}
                </TableCell>
                <TableCell className="text-gray-600">
                  {duration(j.started_at, j.finished_at)}
                </TableCell>
                <TableCell className="text-sm text-gray-500">
                  {fmtDate(j.started_at)}
                </TableCell>
                <TableCell>
                  {j.error ? (
                    <span className="flex items-center gap-1 text-red-500 text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate max-w-[200px]">
                        {j.error.split('\n')[0]}
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const SourcesPage: React.FC = () => {
  usePageTitle('Sources');
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    sources,
    loading,
    error,
    fetchSources,
    createSource,
    updateSource,
    deleteSource,
    runSource,
    cancelSource,
    setSources,
  } = useSources();
  const { details: stalenessDetails } = useSourceStaleness();

  // Artifact counts from source health
  const [artifactCounts, setArtifactCounts] = useState<
    Record<
      string,
      {
        eligible: number;
        extracted: number;
        lastStatus: string | null;
        lastRunAt: string | null;
        lastPages: number;
        lastStartedAt: string | null;
        lastFinishedAt: string | null;
      }
    >
  >({});
  const fetchArtifactCounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/source-health');
      if (!res.ok) return;
      const json = await res.json();
      const map: Record<
        string,
        {
          eligible: number;
          extracted: number;
          lastStatus: string | null;
          lastRunAt: string | null;
          lastPages: number;
          lastStartedAt: string | null;
          lastFinishedAt: string | null;
        }
      > = {};
      for (const row of json.data ?? []) {
        map[row.id] = {
          eligible: row.total,
          extracted: row.extracted,
          lastStatus: row.last_status,
          lastRunAt: row.last_run_at,
          lastPages: row.last_pages_crawled ?? 0,
          lastStartedAt: row.last_job_started_at ?? null,
          lastFinishedAt: row.last_job_finished_at ?? null,
        };
      }
      setArtifactCounts(map);
    } catch {
      /* silent */
    }
  }, []);
  useEffect(() => {
    void fetchArtifactCounts();
  }, [fetchArtifactCounts]);
  // Auto-refresh counts alongside sources
  useEffect(() => {
    const interval = setInterval(() => void fetchArtifactCounts(), 30_000);
    return () => clearInterval(interval);
  }, [fetchArtifactCounts]);
  const stalenessMap = new Map(stalenessDetails.map((s) => [s.source_id, s]));
  const [runningId, setRunningId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);

  // Confirmation modal for destructive / override actions
  type PendingAction =
    | { type: 'run'; id: string; name: string; hasSchedule: boolean }
    | { type: 'stop'; id: string; name: string }
    | { type: 'toggle'; id: string; name: string; currentlyEnabled: boolean }
    | { type: 'delete'; id: string; name: string };
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  // Edit-dialog open state keyed by source id (programmatic open on row click)
  const [editOpenId, setEditOpenId] = useState<string | null>(null);
  const [healthSourceId, setHealthSourceId] = useState<string | null>(null);
  const [copySource, setCopySource] = useState<Source | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('q') ?? ''
  );
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get('status') ?? 'all'
  );
  // Checkbox must be ticked before the Archive (delete) button becomes active
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);

  // Live-poll the most recently queued job
  const { status: pollStatus, done: pollDone } = useJobPoller(pollingJobId);

  // Once polling finishes, refresh the source list to show updated last_run_at
  useEffect(() => {
    if (pollDone) {
      void fetchSources();
      setPollingJobId(null);
    }
  }, [pollDone, fetchSources]);

  const canManage = user && user.role === 'SYSTEM_ADMIN';
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [expandedSourceId, setExpandedSourceId] = useState<string | null>(null);

  if (user && !canManage) {
    return <Navigate to="/dashboard" replace />;
  }

  // Client-side filter
  const reviewReqIds = new Set(
    Object.entries(artifactCounts)
      .filter(([, ac]) => {
        const pages = ac.lastPages ?? 0;
        if (pages === 0) return true;
        if (ac.lastStartedAt && ac.lastFinishedAt) {
          const ms =
            new Date(ac.lastFinishedAt).getTime() -
            new Date(ac.lastStartedAt).getTime();
          if (ms < 60_000) return true;
        }
        return false;
      })
      .map(([id]) => id)
  );

  const filteredSources = sources.filter((src) => {
    // Status filter
    if (statusFilter === 'review_req') {
      return reviewReqIds.has(src.id);
    }
    if (statusFilter !== 'all') {
      const stat = src.last_status ?? 'unrun';
      if (stat !== statusFilter) return false;
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      src.name.toLowerCase().includes(q) ||
      src.base_url.toLowerCase().includes(q) ||
      src.source_type.toLowerCase().includes(q)
    );
  });

  const handleRun = async (id: string) => {
    setRunningId(id);
    setRunError(null);
    setPollingJobId(null);
    try {
      const job = await runSource(id);
      if (job?.id) setPollingJobId(job.id as string);
      // Optimistically update status so "Queued" shows immediately
      setSources((prev) =>
        prev.map((s) => (s.id === id ? { ...s, last_status: 'queued' } : s))
      );
    } catch (e: unknown) {
      setRunError(e instanceof Error ? e.message : 'Failed to queue job');
    } finally {
      setRunningId(null);
    }
  };

  const handleCancel = async (id: string, sourceName: string) => {
    console.log(
      `[sources] User cancelled scrape for source ${id} (${sourceName})`
    );
    setCancellingId(id);
    setRunError(null);
    try {
      await cancelSource(id);
      // Optimistically clear queued status immediately
      setSources((prev) =>
        prev.map((s) => (s.id === id ? { ...s, last_status: null } : s))
      );
      await fetchSources();
    } catch (e: unknown) {
      setRunError(e instanceof Error ? e.message : 'Failed to cancel job');
    } finally {
      setCancellingId(null);
    }
  };

  const handleCopy = (src: Source) => {
    setCopySource({
      ...src,
      name: `${src.name} (copy)`,
    });
  };

  /** Confirm and execute the pending action, then dismiss the modal. */
  const confirmPendingAction = async () => {
    if (!pendingAction) return;
    const action = pendingAction;
    setPendingAction(null);
    if (action.type === 'run') {
      await handleRun(action.id);
    } else if (action.type === 'stop') {
      await handleCancel(action.id, action.name);
    } else if (action.type === 'delete') {
      await deleteSource(action.id);
    } else if (action.type === 'toggle') {
      await handleToggleActive(sources.find((s) => s.id === action.id)!);
    }
  };

  const handleToggleActive = async (src: Source) => {
    await updateSource(src.id, { enabled: !src.enabled });
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-7 h-7 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Data Sources</h1>
          </div>
          <p className="text-gray-500">
            Register county / government data sources and manage scheduled
            scraping.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchSources()}
            className="border-gray-300 text-gray-900 bg-white hover:bg-gray-100"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <SourceDialog
            title="Add source"
            description="Register a new county or government data source for ingestion."
            onSave={(data) =>
              createSource({
                name: data.name,
                base_url: data.url,
                source_type: data.source_type,
                schedule_cron: toScheduleCron(data.schedule_cron) ?? undefined,
                crawl_config: buildCrawlConfig(data.crawl),
                jurisdiction_id: data.jurisdiction_id || null,
                jurisdiction_fips: data.jurisdiction_fips || null,
              })
            }
            trigger={
              <Button>
                <Plus className="w-4 h-4 mr-1" /> Add source
              </Button>
            }
          />
        </div>
      </div>

      {runError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          {runError}
        </div>
      )}

      {pollingJobId && !pollDone && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3 text-sm">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          Job queued — status: <strong>{pollStatus ?? 'pending'}</strong>
        </div>
      )}

      {/* ── Sources table ── */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <CardTitle className="mb-2 text-red-900">
              Failed to Load Sources
            </CardTitle>
            <CardDescription className="mb-4 text-center max-w-md text-red-700">
              {error}
            </CardDescription>
            <Button onClick={() => void fetchSources()}>
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      ) : sources.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Globe className="w-12 h-12 text-gray-300 mb-4" />
            <CardTitle className="mb-2">No sources registered</CardTitle>
            <CardDescription className="mb-6">
              Add your first county or government data source to start ingesting
              documents.
            </CardDescription>
            <SourceDialog
              title="Add source"
              description="Register a new county or government data source."
              onSave={(data) =>
                createSource({
                  name: data.name,
                  base_url: data.url,
                  source_type: data.source_type,
                  schedule_cron:
                    toScheduleCron(data.schedule_cron) ?? undefined,
                  crawl_config: buildCrawlConfig(data.crawl),
                  jurisdiction_id: data.jurisdiction_id || null,
                  jurisdiction_fips: data.jurisdiction_fips || null,
                })
              }
              trigger={
                <Button>
                  <Plus className="w-4 h-4 mr-1" /> Add source
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden text-gray-900">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Filter by name, URL or type…"
                value={searchQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchQuery(v);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (v.trim()) {
                      next.set('q', v);
                    } else {
                      next.delete('q');
                    }
                    return next;
                  });
                }}
                className="pl-9 pr-8 bg-white text-gray-900 caret-gray-900 placeholder:text-gray-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('q');
                      return next;
                    });
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          {/* Quick filter pills */}
          <div className="px-4 py-2 border-b border-gray-200 bg-white flex items-center gap-1.5 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'unrun', label: 'Unrun' },
              { key: 'running', label: 'Running' },
              { key: 'queued', label: 'Queued' },
              { key: 'succeeded', label: 'Done' },
              { key: 'review_req', label: 'Review Req' },
              { key: 'failed', label: 'Failed' },
            ].map(({ key, label }) => {
              const count =
                key === 'all'
                  ? sources.length
                  : key === 'review_req'
                    ? reviewReqIds.size
                    : sources.filter((s) => (s.last_status ?? 'unrun') === key)
                        .length;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setStatusFilter(key);
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      if (key === 'all') {
                        next.delete('status');
                      } else {
                        next.set('status', key);
                      }
                      return next;
                    });
                  }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    statusFilter === key
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                  <span className="ml-1 opacity-50">({count})</span>
                </button>
              );
            })}
          </div>
          <div className="max-h-[62vh] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-gray-100 border-b-2 border-gray-300 hover:bg-gray-100 pointer-events-none">
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Name
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    URL
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Schedule
                  </TableHead>
                  <TableHead className="text-right text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Artifacts
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide w-32">
                    Status
                  </TableHead>
                  <TableHead className="text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Last scraped
                  </TableHead>
                  <TableHead className="text-right text-gray-800 font-bold text-xs uppercase tracking-wide">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSources.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-gray-500"
                    >
                      No sources match &ldquo;{searchQuery}&rdquo;
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSources.map((src) => (
                    <React.Fragment key={src.id}>
                      <TableRow
                        key={src.id}
                        className={`border-b border-gray-100 transition-colors cursor-pointer ${
                          selectedSourceId === src.id
                            ? 'bg-slate-100 ring-1 ring-inset ring-slate-400'
                            : 'bg-white hover:bg-slate-50'
                        }`}
                        onClick={() => {
                          setSelectedSourceId(src.id);
                          setExpandedSourceId(
                            expandedSourceId === src.id ? null : src.id
                          );
                        }}
                      >
                        <TableCell className="font-semibold text-gray-900">
                          <span className="flex items-center gap-2">
                            {expandedSourceId === src.id ? (
                              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                            )}
                            {src.name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <a
                            href={src.base_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline text-sm truncate max-w-[200px] block"
                          >
                            {src.base_url}
                          </a>
                        </TableCell>
                        <TableCell>
                          {/* pointer-events-none keeps the pill from changing color on hover */}
                          <Badge
                            variant="secondary"
                            className="capitalize bg-gray-100 text-gray-800 border border-gray-300 pointer-events-none"
                          >
                            {src.source_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {src.schedule_cron
                            ? (SCHEDULES.find(
                                (s) => s.value === src.schedule_cron
                              )?.label ?? src.schedule_cron)
                            : 'Manual'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-gray-700 tabular-nums">
                          {artifactCounts[src.id]?.eligible != null
                            ? artifactCounts[src.id].eligible.toLocaleString()
                            : '\u2014'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <span className="pointer-events-none">
                            {(() => {
                              const ac = artifactCounts[src.id];
                              const status = ac?.lastStatus || src.last_status;
                              // Check if latest job needs review
                              const lastPages = ac?.lastPages ?? 0;
                              const terminated =
                                status &&
                                status !== 'queued' &&
                                status !== 'running';
                              const shortRun =
                                terminated &&
                                ac?.lastStartedAt &&
                                ac?.lastFinishedAt &&
                                new Date(ac.lastFinishedAt).getTime() -
                                  new Date(ac.lastStartedAt).getTime() <
                                  60000;
                              if (
                                (lastPages === 0 || shortRun) &&
                                status &&
                                terminated
                              ) {
                                return (
                                  <Badge
                                    variant="outline"
                                    className="border-orange-300 bg-orange-50 text-orange-700 shrink-0 whitespace-nowrap"
                                  >
                                    Review Req
                                  </Badge>
                                );
                              }
                              if (status) return statusBadge(status);
                              if (src.enabled)
                                return (
                                  <span className="flex items-center gap-1 text-green-700 text-sm font-medium">
                                    <CheckCircle2 className="w-4 h-4" /> Active
                                  </span>
                                );
                              return (
                                <span className="flex items-center gap-1 text-gray-500 text-sm">
                                  <XCircle className="w-4 h-4" /> Paused
                                </span>
                              );
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {fmtDate(src.last_run_at)}
                        </TableCell>
                        <TableCell
                          className="text-right space-x-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Run / Stop — opens confirmation modal */}
                          {['running', 'queued', 'paused'].includes(
                            artifactCounts[src.id]?.lastStatus ||
                              src.last_status ||
                              ''
                          ) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={cancellingId === src.id}
                              onClick={() =>
                                setPendingAction({
                                  type: 'stop',
                                  id: src.id,
                                  name: src.name,
                                })
                              }
                              title="Stop running scrape"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              {cancellingId === src.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={runningId === src.id}
                              onClick={() =>
                                setPendingAction({
                                  type: 'run',
                                  id: src.id,
                                  name: src.name,
                                  hasSchedule: !!src.schedule_cron,
                                })
                              }
                              title={
                                src.schedule_cron
                                  ? 'Run now — overrides scheduled scrape'
                                  : 'Run scrape now'
                              }
                              className="text-green-600 hover:text-green-800 hover:bg-green-50"
                            >
                              {runningId === src.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {/* Pause / Activate — opens confirmation modal */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setPendingAction({
                                type: 'toggle',
                                id: src.id,
                                name: src.name,
                                currentlyEnabled: src.enabled,
                              })
                            }
                            title={
                              src.enabled ? 'Pause source' : 'Activate source'
                            }
                            className={
                              src.enabled
                                ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                                : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                            }
                          >
                            {src.enabled ? (
                              <XCircle className="w-4 h-4" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                          </Button>
                          {/* Edit — programmatically opened by row click; also accessible via pencil */}
                          <SourceDialog
                            title="Edit source"
                            description="Update the source configuration."
                            initial={src}
                            open={editOpenId === src.id}
                            onOpenChange={(open) => {
                              if (!open) setEditOpenId(null);
                            }}
                            onSave={(data) =>
                              updateSource(src.id, {
                                name: data.name,
                                base_url: data.url,
                                source_type: data.source_type,
                                schedule_cron: toScheduleCron(
                                  data.schedule_cron
                                ),
                                crawl_config: buildCrawlConfig(data.crawl),
                                ...(data.jurisdiction_id && {
                                  jurisdiction_id: data.jurisdiction_id,
                                }),
                                ...(data.jurisdiction_fips && {
                                  jurisdiction_fips: data.jurisdiction_fips,
                                }),
                              })
                            }
                            trigger={
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Edit"
                                className="text-gray-500 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => setEditOpenId(src.id)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            }
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Source health"
                            className="text-gray-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => setHealthSourceId(src.id)}
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Copy source"
                            className="text-gray-500 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleCopy(src)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() =>
                              setPendingAction({
                                type: 'delete',
                                id: src.id,
                                name: src.name,
                              })
                            }
                            title="Archive source"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedSourceId === src.id && (
                        <TableRow key={src.id + '-jobs'} className="bg-gray-50">
                          <TableCell colSpan={7} className="p-0">
                            <div className="max-h-64 overflow-y-auto border-t border-gray-200">
                              <JobsPanel sourceId={src.id} />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Action confirmation modal ── */}
      <Dialog
        open={!!pendingAction}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAction(null);
            setDeleteConfirmChecked(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-sm bg-white text-gray-900 border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">
              {pendingAction?.type === 'stop' && 'Stop scrape?'}
              {pendingAction?.type === 'run' && 'Run scrape now?'}
              {pendingAction?.type === 'delete' && 'Archive source?'}
              {pendingAction?.type === 'toggle' &&
                (pendingAction.currentlyEnabled
                  ? 'Pause source?'
                  : 'Activate source?')}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-gray-500 space-y-3">
                {pendingAction?.type === 'stop' && (
                  <p>
                    This will cancel the currently running scrape for{' '}
                    <span className="font-medium text-gray-800">
                      {pendingAction.name}
                    </span>
                    . Any pages already crawled will be kept.
                  </p>
                )}
                {pendingAction?.type === 'run' && (
                  <p>
                    Start an immediate scrape of{' '}
                    <span className="font-medium text-gray-800">
                      {pendingAction.name}
                    </span>
                    {pendingAction.hasSchedule && (
                      <>
                        {' '}
                        — this will run{' '}
                        <span className="font-medium">in addition to</span> any
                        scheduled scrapes.
                      </>
                    )}
                  </p>
                )}
                {pendingAction?.type === 'delete' && (
                  <>
                    <p>
                      <span className="font-medium text-gray-800">
                        {pendingAction.name}
                      </span>{' '}
                      will be <span className="font-medium">archived</span>{' '}
                      (disabled).
                    </p>
                    {/* Hard-guard explanation */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 space-y-1">
                      <p className="font-semibold flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        Data is never permanently deleted
                      </p>
                      <p className="text-amber-700 text-xs">
                        The system has a hard guard that prevents artifact loss.
                        Archiving a source only disables it — all{' '}
                        <span className="font-medium">
                          collected artifacts, embeddings, and processing
                          history are fully preserved
                        </span>{' '}
                        and remain searchable. You can re-enable the source at
                        any time from the edit dialog.
                      </p>
                    </div>
                    {/* Mandatory checkbox */}
                    <label className="flex items-start gap-2.5 cursor-pointer select-none text-sm text-gray-700 pt-1">
                      <input
                        type="checkbox"
                        checked={deleteConfirmChecked}
                        onChange={(e) =>
                          setDeleteConfirmChecked(e.target.checked)
                        }
                        className="mt-0.5 rounded border-gray-400 text-red-600 focus:ring-red-500"
                      />
                      <span>
                        I understand this source will be archived and no new
                        scrapes will run. Existing data is preserved.
                      </span>
                    </label>
                  </>
                )}
                {pendingAction?.type === 'toggle' && (
                  <p>
                    {pendingAction.currentlyEnabled
                      ? 'Pausing will prevent scheduled scrapes from running for '
                      : 'Activating will re-enable scheduled scrapes for '}
                    <span className="font-medium text-gray-800">
                      {pendingAction.name}
                    </span>
                    .
                  </p>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setPendingAction(null);
                setDeleteConfirmChecked(false);
              }}
              className="border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setDeleteConfirmChecked(false);
                void confirmPendingAction();
              }}
              disabled={
                pendingAction?.type === 'delete' && !deleteConfirmChecked
              }
              className={
                pendingAction?.type === 'stop' ||
                pendingAction?.type === 'delete'
                  ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }
            >
              {pendingAction?.type === 'stop' && 'Stop scrape'}
              {pendingAction?.type === 'run' && 'Run now'}
              {pendingAction?.type === 'delete' && 'Archive source'}
              {pendingAction?.type === 'toggle' &&
                (pendingAction?.currentlyEnabled ? 'Pause' : 'Activate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Copy source dialog ── */}
      {copySource && (
        <SourceDialog
          title={`Copy: ${copySource.name}`}
          description="Adjust the copied source's details then save."
          initial={copySource}
          open={true}
          onOpenChange={(open) => {
            if (!open) setCopySource(null);
          }}
          onSave={async (data) => {
            if (!data.jurisdiction_id && !copySource.jurisdiction_id) {
              throw new Error(
                'A jurisdiction is required. Select one from the jurisdiction field before saving.'
              );
            }
            // Warn if the URL domain changed but the jurisdiction is still the
            // original — catches mismatched jurisdictions from copy (e.g. copying
            // a La Crescent source but changing the URL to an Arkansas city).
            if (data.jurisdiction_id === copySource.jurisdiction_id) {
              try {
                const oldDomain = new URL(copySource.base_url).hostname.replace(
                  /^www\./,
                  ''
                );
                const newDomain = new URL(data.url).hostname.replace(
                  /^www\./,
                  ''
                );
                if (
                  oldDomain !== newDomain &&
                  !window.confirm(
                    `The URL domain changed (${oldDomain} → ${newDomain}) but the jurisdiction stayed the same.\n\nAre you sure the jurisdiction is correct for this source?`
                  )
                ) {
                  return;
                }
              } catch {
                /* URL parse failure — skip the check */
              }
            }
            await createSource({
              name: data.name,
              base_url: data.url,
              source_type: data.source_type,
              schedule_cron: toScheduleCron(data.schedule_cron) ?? undefined,
              crawl_config: buildCrawlConfig(data.crawl),
              jurisdiction_id:
                data.jurisdiction_id || copySource.jurisdiction_id,
              jurisdiction_fips: data.jurisdiction_fips || null,
            });
            setCopySource(null);
            await fetchSources();
          }}
          trigger={<span />}
        />
      )}
      {healthSourceId && (
        <SourceHealthModal
          sourceId={healthSourceId}
          sourceName={sources.find((s) => s.id === healthSourceId)?.name ?? ''}
          sourceUrl={
            sources.find((s) => s.id === healthSourceId)?.base_url ?? ''
          }
          staleness={stalenessMap.get(healthSourceId)}
          open={true}
          onClose={() => setHealthSourceId(null)}
        />
      )}
    </div>
  );
};
